
import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, BrandingStyle, useBCMSContext, useModel, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import Util from "classes/Util";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import { PrimaryButton, SecondaryButton, UIAmountInput } from "components/ui";
import Classes, { Account, Deposit } from "model";
import { ImageAsset, SVGAsset } from "res";
import CheckPictureWidget, { BackCheckPictureWidget, FrontCheckPictureWidget } from "./components/CheckPictureWidget";
import "./styles.css";

export default function DepositACheckPage(props) {
    const { goStep, nextStep } = useWorkflow() ?? {};
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const navigate = useRoute();
    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    // Show the mabi23 bottom in this page. It will be hidden in the rest of the pages
    // in this workflow where the camera view takes up the screen.
    Util.hideMABI23BottomNav(false);

    const state = { data, ui };
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSelectChange: value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "deposit", "account"], value === "selectAnAccount" ? undefined : value)
                .submit(setState);
        },
        onAmountBlur: value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "deposit", "amount"], value)
                .submit(setState)
        },
        onCheckClick: (value, mobileDepositsVersion) => () => {
            if(mobileDepositsVersion === "check images") {
                const currState = { data: state.data, ui: state.ui };
                
                if(value === "front") {
                    if(state.ui.checkFrontView.isSet) {
                        goStep(4, currState);
                    } else {
                        goStep(3, currState);
                    }
                } else if(value === "back") {
                    goStep(6, currState);
                }
            } else {
                BCMSStateManagerCRUD.init({ ui: state.ui, data: state.data })
                .update(["ui", "selectedCheckView"], value)
                .update(["data", "deposit"], data.deposit)
                .submit(nextStep);
            }

            // Sends a message to mabi23 to hide the bottom nav.
            Util.hideMABI23BottomNav(true);
        },

        onCancelClick: (mobileDepositsVersion) => () => {
            if(mobileDepositsVersion === "check images") {
                BCMSStateManagerCRUD.init({ ui: state.ui, data: state.data })
                .update([ "ui" ], getDefaultPageUI())
                .update([ "data" ], getDefaultPageData())
                .submit(setState);
            } else {
                navigate("/accountSummary");
            }
        },

        onDepositClick: () => {
            const defaultDeposit = BCMSDefaultModel.deposit();
            const mergedRecord = BCMSDefaultModel.mergeRecords(data.deposit, defaultDeposit);
            mergedRecord.id = Deposit.nextID();

            BCMSRecord.batch([
                Deposit.create(mergedRecord)
            ], globalController, Classes);

            const result = BCMSStateManagerCRUD.init({ ui: state.ui, data: state.data })
            .update(["data", "deposit"], Deposit.init(mergedRecord))
            .submit();
            goStep(2, result);
        },

        viewDepositHistoryClick: () => navigate("/depositHistory")
    });

    return (
        <PageContainer className="deposit-a-check-page" state={state} controller={controller}>
            <DepositACheckContent />
        </PageContainer>
    );
}

export const getDefaultPageData = () => ({
    deposit: Deposit.init({})
});
export const getDefaultPageUI = () => ({
    accountDropdown: { isOpen: false },
    checkFrontView: { isSet: false },
    checkBackView: { isSet: false },
    selectedCheckView: "front" // either "front" or "back".
});

function DepositACheckContent(props) {
    const { data, ui, onAmountBlur, onCancelClick, onDepositClick, viewDepositHistoryClick} = useStateManager();
    const { branding } = useModel();
    const { options } = branding;
    const depositACheckPromoText = options.mobile_deposits_promo_abi22;
    const mobileDepositsSubheader = options.mobile_deposits_subheader_abi22;
    const deposit = data.deposit;
    const amount = deposit.amount.amount ? deposit.amount.format().slice(1) : "";
    const accountID = deposit.account ? deposit.account.id : undefined;
    const isDisabled = deposit.amount.amount !== undefined && accountID !== undefined && ui.checkFrontView.isSet && ui.checkBackView.isSet;
    const subfooterText = options.deposits_subfooter_abi22;
    
    // Note, that "window.mabi23Branding" is set after "PageContainer" above renders,
    // so this code can't be used until after that component is initialized.
    const windowMabi23Branding = window.mabi23Branding;
    let mabi23Branding = { colors: {}, options: {} };
    let mabi23BrandingOptions = { options: {} };

    if(windowMabi23Branding) {
        mabi23Branding = windowMabi23Branding;
        mabi23BrandingOptions = mabi23Branding.options;
    }

    let mobileDepositsVersion = mabi23BrandingOptions.mobile_deposits_version_mabi23;
    mobileDepositsVersion = mobileDepositsVersion === undefined || mobileDepositsVersion === "undefined" ? "Front & Back Images" : mobileDepositsVersion;
    mobileDepositsVersion = mobileDepositsVersion.toLowerCase();

    return (
        <PageContentView>
            <div className="flex-container flex-vertical page-widget-wrapper">
                <div class="deposit-a-check-promo-text-label" dangerouslySetInnerHTML={{ __html: depositACheckPromoText }}></div>
                <CenterContentContainer>
                    <div className="endorsement-instructions-container hide">
                        <div className="flex-container flex-vertical">
                            <p className="text-center endorse-text">Please endorse the back of the check as shown below:</p>
                            <b className="text-center mobile-deposit-text" data-point="Deposit_For_Mobile">For mobile deposit only</b>
                            <i className="text-center your-name-here-text">[Your Name Here]</i>
                        </div>
                    </div>
                    <ContentHeaderView text="Deposit A Check" dataPoint="Deposit_Check_Title" />
                    <div class="mobile-deposits-subheader-label" dangerouslySetInnerHTML={{ __html: mobileDepositsSubheader }}></div>

                    <FormContainer className="deposit-a-check-form">
                        <FormRowView text="* Deposit To">
                            <DepositAccountSelect dataPoint="Deposit_Account_Select" />
                        </FormRowView>

                        <FormRowView text="Check Amount">
                            <UIAmountInput value={amount} onBlur={onAmountBlur} dataPoint="Deposit_Amount" />
                        </FormRowView>
                        { mabi23BrandingOptions.endorsement_text_mabi23 && 
                        <div className="endorsement-text-container">
                            { mabi23BrandingOptions.endorsement_text_mabi23 }
                        </div>
                        }   
                        <div className="flex-container flex-horizontal align-center space-between">
                            <CheckFrontImage mobileDepositsVersion={ mobileDepositsVersion } />
                            <CheckBackImage mobileDepositsVersion={ mobileDepositsVersion } />
                        </div>

                        <FormButtonRow>
                            <SecondaryButton text="Cancel" onClick={ onCancelClick(mobileDepositsVersion) }></SecondaryButton>
                            <PrimaryButton text="Deposit" className={!isDisabled ? "disabled-button" : ""} onClick={onDepositClick} dataPoint="Deposit_Done"></PrimaryButton>
                        </FormButtonRow>

                        <FormButtonRow className="deposit-history-button-container">
                            <SecondaryButton text="View Deposit History" dataPoint="Deposit_View_History" onClick={viewDepositHistoryClick}></SecondaryButton>
                        </FormButtonRow>
                    </FormContainer>
                </CenterContentContainer>
                { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
            </div>
        </PageContentView>
    );
}

function CheckFrontImage(props) {
    const { data, ui, onCheckClick } = useStateManager();
    const { isSet } = ui.checkFrontView;
    const mobileDepositsVersion = props.mobileDepositsVersion ?? "";

    return (
        <>
            { isSet &&
                <CheckPictureWidget text="Front of Check"
                    checkWorkflow={<><FrontCheckPictureWidget dataPoint="Deposit_Front_Check" image={<><ImageAsset id="CheckFront" className="check-image check" /></>}
                        onClick={  onCheckClick("front", mobileDepositsVersion) } /></>} />
            }

            { !isSet && mobileDepositsVersion === "front & back images" &&
                <CheckPictureWidget text="Front of Check"
                    checkWorkflow={<><FrontCheckPictureWidget dataPoint="Deposit_Front_Check" image={<><BrandingStyle style={{ "fill" : "links_abi22" }} element={ <SVGAsset id="CameraIcon" /> } /></>}
                        onClick={ onCheckClick("front", mobileDepositsVersion) } /></>} />
            }

            { !isSet && mobileDepositsVersion === "check images" &&
                <CheckPictureWidget text={ `Check Images` } textClassName="text-left"
                    checkWorkflow={<><FrontCheckPictureWidget dataPoint="Deposit_Front_Check" image={<><BrandingStyle style={{ "fill" : "links_abi22" }} element={ <SVGAsset id="CameraIcon" /> } /></>}
                        onClick={ onCheckClick("front", mobileDepositsVersion) } /></>} />
            }
        </>
    );
}

function CheckBackImage(props) {
    const { ui, onCheckClick } = useStateManager();
    const { isSet } = ui.checkBackView;
    const mobileDepositsVersion = props.mobileDepositsVersion ?? "";

    return (
        <>
        { mobileDepositsVersion === "front & back images" && 
            <>
                { isSet &&
                    <CheckPictureWidget text="Back of Check"
                        checkWorkflow={<><BackCheckPictureWidget dataPoint="Deposit_Back_Check" image={<><ImageAsset id="CheckBack" className="check-image check" /></>}
                            onClick={ onCheckClick("back", mobileDepositsVersion) } /></>} />
                }

                { !isSet &&
                    <CheckPictureWidget text="Back of Check"
                        checkWorkflow={<><BackCheckPictureWidget dataPoint="Deposit_Back_Check" image={<><BrandingStyle style={{ "fill" : "links_abi22" }} element={ <SVGAsset id="CameraIcon" /> } /></>}
                            onClick={ onCheckClick("back", mobileDepositsVersion) } /></>} />
                }
            </>
        }
        { mobileDepositsVersion === "check images" &&
            <>
                { isSet &&
                    <CheckPictureWidget text="Back of Check"
                        checkWorkflow={<><BackCheckPictureWidget dataPoint="Deposit_Back_Check" image={<><ImageAsset id="CheckBack" className="check-image check" /></>}
                            onClick={ onCheckClick("back", mobileDepositsVersion) } /></>} />
                }
            </>
        }
        </>
    );
}

function DepositAccountSelect(props) {
    const { onSelectChange, data, ui } = useStateManager();

    const value = data.deposit.account ? data.deposit.account.id.toString() : "selectAnAccount";
    const { isOpen } = ui.accountDropdown;

    const accountItems = Account.initAll().reduce((result, account) => {
        if(account.type.toLowerCase() === "loan") return result;
        let description = `${account.number} - ${account.nickname} (Available ${ Account.isLOC(account) ? account.availableCredit.format() : account.currentBalance.format() })`;

        return [...result, <CustomSelectItem value={account.id.toString()} key={`Account_${account.id}`} dataPoint={`Account_${account.id}`}>{description}</CustomSelectItem>];
    }, [<CustomSelectItem value="selectAnAccount" key={"selectAnAccount"}>-- Select an Account --</CustomSelectItem>]);

    return (
        <CustomSelect value={value} active={isOpen} onChange={onSelectChange} dataPoint={props.dataPoint}>
            {accountItems}
        </CustomSelect>
    );
}